import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Plus, 
  Phone,
  User,
  Building,
  Pencil,
  Trash2,
  Shield,
  Mail
} from "lucide-react";

export default function TeamPage() {
  const { token, API } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    department: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, deptsRes] = await Promise.all([
        fetch(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/departments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (usersRes.ok) setUsers(await usersRes.json());
      if (deptsRes.ok) {
        const data = await deptsRes.json();
        setDepartments(data.departments || []);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.mobile || !formData.department) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      const url = editingUser ? `${API}/users/${editingUser.id}` : `${API}/users`;
      const method = editingUser ? "PUT" : "POST";
      
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to save user");
      }

      toast.success(editingUser ? "Team member updated" : "Team member added");
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (userId) => {
    if (!confirm("Are you sure you want to delete this team member?")) return;

    try {
      const response = await fetch(`${API}/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error("Failed to delete user");

      toast.success("Team member deleted");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      mobile: "",
      email: "",
      department: ""
    });
    setEditingUser(null);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      mobile: user.mobile,
      email: user.email || "",
      department: user.department
    });
    setShowDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-500">Loading team members...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 ">Team</h1>
          <p className="text-slate-500 mt-1">
            Manage team members and departments
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="rounded-lg gap-2 bg-[#1e3a5f] hover:bg-[#152a47] text-white"
          data-testid="add-member-btn"
        >
          <Plus className="w-5 h-5" />
          Add Member
        </Button>
      </div>

      {/* Team Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.length === 0 ? (
          <Card className="col-span-full bg-white border-slate-200">
            <CardContent className="py-12 text-center">
              <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No team members added yet</p>
            </CardContent>
          </Card>
        ) : (
          users.map((user, index) => (
            <Card 
              key={user.id}
              className="bg-white border-slate-200 hover:border-slate-300 transition-colors card-shadow card-shadow-hover"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`user-card-${user.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-lg flex items-center justify-center">
                      <User className="w-6 h-6 text-[#1e3a5f]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900">{user.name}</h3>
                      <Badge 
                        className={`rounded-md mt-1 ${
                          user.role === 'admin' 
                            ? 'bg-[#1e3a5f]/10 text-[#1e3a5f] border-[#1e3a5f]/20' 
                            : 'bg-slate-100 text-slate-600 border-slate-200'
                        }`}
                      >
                        {user.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                        {user.role}
                      </Badge>
                    </div>
                  </div>
                  {user.role !== 'admin' && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(user)}
                        className="rounded-lg h-8 w-8 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(user.id)}
                        className="rounded-lg h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Phone className="w-4 h-4" />
                    <span>{user.mobile}</span>
                  </div>
                  {user.email && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{user.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-slate-500">
                    <Building className="w-4 h-4" />
                    <span>{user.department}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md bg-white border-slate-200" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900 ">
              {editingUser ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {editingUser ? 'Update team member details' : 'Add a new team member'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-slate-700">Full Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                className="rounded-lg h-11 border-slate-200"
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Mobile Number *</Label>
              <Input
                type="tel"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                placeholder="Enter mobile number"
                className="rounded-lg h-11 border-slate-200"
                disabled={!!editingUser}
                data-testid="user-mobile-input"
              />
              {editingUser && (
                <p className="text-xs text-slate-400">Mobile number cannot be changed</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Email Address</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                className="rounded-lg h-11 border-slate-200"
                data-testid="user-email-input"
              />
              <p className="text-xs text-slate-400">Email is used for task notifications</p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Department *</Label>
              <Select 
                value={formData.department} 
                onValueChange={(value) => setFormData({ ...formData, department: value })}
              >
                <SelectTrigger className="rounded-lg h-11 border-slate-200" data-testid="user-dept-select">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept} className="hover:bg-slate-100">{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowDialog(false)}
                className="rounded-lg border-slate-300"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="rounded-lg bg-[#1e3a5f] hover:bg-[#152a47] text-white"
                data-testid="save-user-btn"
              >
                {editingUser ? 'Update Member' : 'Add Member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
